import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { OutcomeView } from '../components/outcome';
import { ToolForm } from '../components/tool-form';
import { api, type CapabilityMap, type InvokeOutcome, type ToolSpec } from '../lib/api';
import { errorMessage, when } from '../lib/format';

/** D9 setup: add URL → we probe the server → the user reviews the capability map → confirm. */
export function ConnectorsPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const list = useQuery({ queryKey: ['connectors'], queryFn: api.connectors });
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const add = useMutation({
    mutationFn: () => api.addConnector(name.trim(), url.trim()),
    onSuccess: async (r) => {
      setName('');
      setUrl('');
      await qc.invalidateQueries({ queryKey: ['connectors'] });
      await nav({ to: '/connectors/$name', params: { name: r.connector.name } });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    add.mutate();
  };
  return (
    <div className="page stack">
      <h1>Connectors</h1>
      <p className="muted">
        Remote MCP servers your agent can call. Read-only tools are available to the agent in chat;
        tools that place or change orders are only run when you press Execute (D6).
      </p>
      {list.data && list.data.connectors.length === 0 && (
        <p className="muted">No connectors yet.</p>
      )}
      {list.data && list.data.connectors.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Server</th>
              <th>Tools</th>
              <th>Capability map</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {list.data.connectors.map((c) => (
              <tr key={c.id}>
                <td>
                  <Link to="/connectors/$name" params={{ name: c.name }}>
                    <code>{c.name}</code>
                  </Link>
                </td>
                <td>
                  {c.server.name} {c.server.version}
                  <div className="muted">
                    <code>{c.url}</code>
                  </div>
                </td>
                <td>
                  {c.toolCount} ({c.capabilityMap.orderPlacing.length} order-placing)
                </td>
                <td>
                  <span
                    className={
                      c.capabilityMap.source === 'user_confirmed' ? 'pill live' : 'pill busy'
                    }
                  >
                    {c.capabilityMap.source === 'user_confirmed'
                      ? 'confirmed'
                      : `${c.capabilityMap.source}, review`}
                  </span>
                </td>
                <td className={c.status === 'active' ? 'ok' : 'error'}>
                  {c.status}
                  {c.lastError && <div className="muted">{c.lastError}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form onSubmit={submit} className="panel stack">
        <h2 style={{ margin: 0 }}>Add a connector</h2>
        <div className="row wrap">
          <label className="field" style={{ width: 180 }}>
            <span>Name (used in chat, e.g. haipa)</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              pattern="[a-z][a-z0-9_\-]{0,31}"
              required
              placeholder="haipa"
            />
          </label>
          <label className="field grow">
            <span>MCP URL (https; may include the api_key query parameter)</span>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
              type="url"
              placeholder="https://…/mcp/…?api_key=…"
            />
          </label>
        </div>
        <p className="muted">
          The URL is sealed at rest and never shown again in full; only the host is displayed. We
          connect once now to read the tool list.
        </p>
        <div className="row">
          <button type="submit" className="primary" disabled={add.isPending || !name || !url}>
            {add.isPending ? 'Connecting…' : 'Connect'}
          </button>
          {add.error && <span className="error">{errorMessage(add.error)}</span>}
        </div>
      </form>
    </div>
  );
}

const ROLE_LABELS: Record<string, string> = {
  market_lookup: 'market lookup',
  candles: 'candles / chart',
  fills: 'fills / trades',
  quote: 'quotes',
  account: 'account & positions',
  orders: 'open orders',
  algos: 'algos (read)',
  docs: 'docs',
};

export function ConnectorDetailPage() {
  const { name } = useParams({ from: '/app/connectors/$name' });
  const qc = useQueryClient();
  const nav = useNavigate();
  const q = useQuery({ queryKey: ['connector', name], queryFn: () => api.connector(name) });
  const [draft, setDraft] = useState<CapabilityMap | null>(null);
  const [execTool, setExecTool] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<InvokeOutcome | null>(null);

  useEffect(() => {
    if (q.data) setDraft(q.data.connector.capabilityMap);
  }, [q.data]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['connector', name] });
    qc.invalidateQueries({ queryKey: ['connectors'] });
  };
  const save = useMutation({
    mutationFn: () => {
      if (!draft) throw new Error('nothing to save');
      return api.setMap(name, { ...draft, source: 'user_confirmed' });
    },
    onSuccess: invalidate,
  });
  const refresh = useMutation({
    mutationFn: () => api.refreshConnector(name),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: () => api.removeConnector(name),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['connectors'] });
      await nav({ to: '/connectors' });
    },
  });
  const invoke = useMutation({
    mutationFn: (args: Record<string, unknown>) => {
      if (!execTool) throw new Error('no tool selected');
      return api.invoke(name, execTool, args);
    },
    onSuccess: (o) => {
      setOutcome(o);
      qc.invalidateQueries({ queryKey: ['audit'] });
    },
  });

  const tools = q.data?.tools ?? [];
  const roleOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const [role, names] of Object.entries(draft?.roles ?? {}))
      for (const t of names ?? []) m.set(t, role);
    return m;
  }, [draft]);

  if (q.isLoading || !draft) return <div className="page muted">Loading…</div>;
  if (q.error || !q.data) return <div className="page error">{errorMessage(q.error)}</div>;
  const c = q.data.connector;
  const dirty = JSON.stringify(draft) !== JSON.stringify(c.capabilityMap);
  const toggleOrderPlacing = (tool: string, on: boolean) =>
    setDraft((d) =>
      d
        ? {
            ...d,
            orderPlacing: on
              ? [...new Set([...d.orderPlacing, tool])]
              : d.orderPlacing.filter((t) => t !== tool),
          }
        : d,
    );
  const selected: ToolSpec | undefined = tools.find((t) => t.name === execTool);

  return (
    <div className="page stack">
      <div className="row">
        <h1 style={{ margin: 0 }}>
          <Link to="/connectors">Connectors</Link> / <code>{c.name}</code>
        </h1>
        <span className="grow" />
        <button
          type="button"
          className="small"
          disabled={refresh.isPending}
          onClick={() => refresh.mutate()}
        >
          Refresh tools
        </button>
        <button
          type="button"
          className="small danger"
          disabled={remove.isPending}
          onClick={() => {
            if (confirm(`Remove connector "${c.name}"? The agent loses access immediately.`))
              remove.mutate();
          }}
        >
          Remove
        </button>
      </div>
      <p className="muted">
        {c.server.name} {c.server.version} at <code>{c.url}</code> · {tools.length} tools · updated{' '}
        {when(c.updatedAt)}
        {c.lastError && <span className="error"> · {c.lastError}</span>}
      </p>

      <section className="panel stack">
        <h2 style={{ margin: 0 }}>Capability map</h2>
        <p className="muted">
          Tick every tool that can place, change or cancel orders or positions. The agent will never
          call a ticked tool on its own; it asks you to Execute it instead. Untick only tools you
          are sure are read-only. Current source:{' '}
          <b>
            {c.capabilityMap.source === 'user_confirmed'
              ? 'confirmed by you'
              : c.capabilityMap.source}
          </b>
          .
        </p>
        <div>
          {tools.map((t) => {
            const placing = draft.orderPlacing.includes(t.name);
            const role = roleOf.get(t.name);
            return (
              <div className="toolrow" key={t.name}>
                <div>
                  <div className="row">
                    <span className="name">{t.name}</span>
                    {role && <span className="pill">{ROLE_LABELS[role] ?? role}</span>}
                    {!role && !placing && <span className="pill">unclassified</span>}
                  </div>
                  <div className="muted">{t.description ?? ''}</div>
                </div>
                <div className="row">
                  <label className="check">
                    <input
                      type="checkbox"
                      checked={placing}
                      onChange={(e) => toggleOrderPlacing(t.name, e.target.checked)}
                    />
                    order-placing
                  </label>
                  <button
                    type="button"
                    className="small"
                    onClick={() => {
                      setExecTool(t.name);
                      setOutcome(null);
                    }}
                  >
                    Execute…
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="row">
          <button
            type="button"
            className="primary"
            disabled={save.isPending || (!dirty && c.capabilityMap.source === 'user_confirmed')}
            onClick={() => save.mutate()}
          >
            {c.capabilityMap.source === 'user_confirmed' && !dirty ? 'Confirmed' : 'Confirm map'}
          </button>
          {dirty && (
            <button type="button" className="small" onClick={() => setDraft(c.capabilityMap)}>
              Reset
            </button>
          )}
          {save.error && <span className="error">{errorMessage(save.error)}</span>}
        </div>
      </section>

      {selected && (
        <section className="execute stack">
          <h3>
            Execute{' '}
            <code>
              {c.name}.{selected.name}
            </code>
            {draft.orderPlacing.includes(selected.name) && ' (order-placing)'}
          </h3>
          {selected.description && <p className="muted">{selected.description}</p>}
          <ToolForm
            key={selected.name}
            schema={selected.inputSchema}
            busy={invoke.isPending}
            submitLabel={invoke.isPending ? 'Running…' : 'Execute now'}
            onSubmit={(args) => invoke.mutate(args)}
          />
          {invoke.error && <p className="error">{errorMessage(invoke.error)}</p>}
          {outcome && <OutcomeView outcome={outcome} />}
          <button type="button" className="small" onClick={() => setExecTool(null)}>
            Close
          </button>
        </section>
      )}

      {q.data.instructions && (
        <details className="panel">
          <summary className="muted">Server instructions (third-party text, shown as-is)</summary>
          <pre>{q.data.instructions}</pre>
        </details>
      )}
    </div>
  );
}
